import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import type { Credentials, ConnectionInfo, SeoPlugin, PageBuilder } from "../types";
import { describeConnection } from "../lib/fixExecutor";
import { listSites, getSite, addSite, removeSite, type SiteSummary } from "../lib/authClient";
import { fetchAuditList, siteKey, getActiveSiteKey } from "../lib/siteData";
import { showAlert, showConfirm } from "../lib/dialog";

const PLUGIN_LABEL: Record<SeoPlugin, string> = {
  rankmath: "RankMath",
  yoast: "Yoast",
  aioseo: "All in One SEO",
  seopress: "SEOPress",
  unknown: "No SEO plugin detected",
};
const pluginLabel = (p?: SeoPlugin) => PLUGIN_LABEL[p ?? "unknown"] ?? "Unknown SEO plugin";

const BUILDER_LABEL: Record<PageBuilder, string> = {
  elementor: "Elementor",
  divi: "Divi",
  bricks: "Bricks",
  beaver: "Beaver Builder",
  gutenberg: "Block/Classic Editor",
  unknown: "Unknown builder",
};
const builderLabel = (b?: PageBuilder) => BUILDER_LABEL[b ?? "unknown"] ?? "Unknown builder";

interface Props {
  creds: Credentials;
  onChange: (c: Credentials) => void;
  info: ConnectionInfo | null;
  connected: boolean;
  onConnect: (info: ConnectionInfo) => void;
  onDisconnect?: () => void;
  coverMode?: boolean;
  connectOnly?: boolean; // coverMode: show only Site Storage + WP Connection (no Run Full Audit block)
  onRunAudit?: () => void;
  onStopAudit?: () => void;
  onResumeAudit?: () => void;
  auditRunning?: boolean;
  auditPaused?: boolean; // a prior audit was paused (tab closed/logout) → show "Resume Audit"
  auditProgress?: number; // 0–100 while a full audit runs → fills the button as a progress bar
  onSwitchSite?: (key: string, opts?: { siteUrl?: string; label?: string }) => void;
  sitesRefreshToken?: number;
  onSaveReport?: (format: "pdf" | "html") => void;
  canSaveReport?: boolean;
  canRunAudit?: boolean; // false → grey out Run Full Audit until a Get Competitors crawl has completed
  connecting?: boolean; // auto-connecting after a site switch/refresh → show a Site Storage spinner
}

export function CredentialsBar({ creds, onChange, info, connected, onConnect, onDisconnect, coverMode, connectOnly, onRunAudit, onStopAudit, onResumeAudit, auditRunning, auditPaused, auditProgress = 0, onSwitchSite, sitesRefreshToken, onSaveReport, canSaveReport, canRunAudit, connecting }: Props) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(!connected);
  // Hover state for the Connect button: when connected, hovering it offers "Disconnect" (red).
  const [hoverConn, setHoverConn] = useState(false);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [showSaveTip, setShowSaveTip] = useState(false);
  const [showSiteSaveTip, setShowSiteSaveTip] = useState(false);
  // Follow the app theme so popups (save tooltip + export menu) match light mode.
  const [isLight, setIsLight] = useState(() => document.documentElement.getAttribute("data-theme") === "light");
  useEffect(() => {
    const sync = () => setIsLight(document.documentElement.getAttribute("data-theme") === "light");
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  // Collapse the credential form to the compact "Connected" view whenever a connection succeeds —
  // including the automatic reconnect on load/site-switch, which sets `connected` without going
  // through the manual Connect button (that path is the only other place `open` is cleared).
  useEffect(() => { if (connected) setOpen(false); }, [connected]);
  // Cover Version A: the Run Full Audit control is portaled into the Launch panel (step 3) instead
  // of hanging off the top-right connection block. Locate its slot in the cover HTML once mounted.
  const [runSlot, setRunSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!coverMode || connectOnly) { setRunSlot(null); return; }
    setRunSlot(document.getElementById("cover-run-audit-slot"));
  }, [coverMode, connectOnly, sitesRefreshToken]);
  // Shared light/dark palette for the save tooltip + export menu popups.
  const pop = isLight
    ? { bg: "#ffffff", border: "1px solid #e2e8f0", text: "#334155", shadow: "0 8px 24px rgba(15,23,42,.18)", sep: "#e2e8f0", heading: "#94a3b8", hover: "#f1f5f9" }
    : { bg: "#0f172a", border: "1px solid rgba(255,255,255,.12)", text: "#e2e8f0", shadow: "0 8px 24px rgba(0,0,0,.45)", sep: "rgba(255,255,255,.06)", heading: "rgba(255,255,255,.3)", hover: "rgba(255,255,255,.08)" };
  // Saving the current credentials to Site Storage (triggered by the save icon next to Site URL).
  const [savingSite, setSavingSite] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saveTick, setSaveTick] = useState(0); // bump to re-fetch the Site Storage dropdown after a save

  async function saveCurrentSite() {
    if (savingSite) return;
    if (!creds.siteUrl || !creds.username || !creds.appPassword) {
      void showAlert("Fill in Site URL, Username, and Application Password before saving.", { title: "Save site" });
      return;
    }
    setSavingSite(true);
    try {
      // Duplicate guard: if a site with the same normalized host is already saved, don't
      // create a second entry — offer to update the stored credentials instead.
      const host = siteKey(creds.siteUrl);
      let existing: SiteSummary | undefined;
      try { existing = (await listSites()).find(s => siteKey(s.siteUrl) === host); } catch { /* ignore — fall through to save */ }
      if (existing) {
        const update = await showConfirm(
          `"${existing.label}" is already saved in Site Storage. Update its saved credentials with the current Username and Application Password?`,
          { title: "Already saved", confirmLabel: "Update credentials", cancelLabel: "Cancel", tone: "info" },
        );
        if (!update) { setSavingSite(false); return; }
        // Remove the old entry first so the same host can't end up with two records
        // (e.g. when the username or URL formatting differs).
        try { await removeSite(existing.id); } catch { /* ignore */ }
      }
      const saved = await addSite({ siteUrl: creds.siteUrl, username: creds.username, appPassword: creds.appPassword });
      setSaveTick(t => t + 1);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1600);
      // Make the just-saved site the ACTIVE one and load its report — same as picking it from the
      // Site Storage dropdown. Switching remounts the app against that site's data; if it's already
      // the active site this no-ops the remount (the save tick above still refreshes the list).
      onSwitchSite?.(siteKey(saved.siteUrl), { siteUrl: saved.siteUrl, label: saved.label });
    } catch (e) {
      void showAlert(e instanceof Error ? e.message : "Could not save site.", { title: "Save site" });
    } finally {
      setSavingSite(false);
    }
  }

  async function connect() {
    if (!creds.siteUrl || !creds.username || !creds.appPassword) return;
    setLoading(true);
    try {
      const info = await describeConnection(creds.siteUrl, creds.username, creds.appPassword);
      onConnect(info);
      if (info.ok) setOpen(false);
    } catch (e: unknown) {
      onConnect({ ok: false, error: e instanceof Error ? e.message : "Connection failed" });
    }
    setLoading(false);
  }

  const fields = [
    { label: "Site URL",              key: "siteUrl"     as const, ph: "https://yoursite.com/",          type: "text"     },
    { label: "Username",              key: "username"    as const, ph: "wp-username",                    type: "text"     },
    { label: "Application Password", key: "appPassword" as const, ph: "xxxx xxxx xxxx xxxx xxxx xxxx",  type: "password" },
  ];

  // ── Cover-mode: vertical panel embedded in the cover header ──
  if (coverMode) {
    const fieldStyle: React.CSSProperties = { width: "100%", background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 5, padding: "5px 9px", color: "#e2e8f0", fontSize: 11, fontFamily: "Roboto Mono, monospace", outline: "none", boxSizing: "border-box" };
    const labelStyle: React.CSSProperties = { display: "block", fontSize: 9, color: "rgba(255,255,255,.25)", textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, fontFamily: "Mulish, sans-serif", marginBottom: 3 };
    return (
      <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 4, width: 400 }}>
        {/* Site Storage (Version A) — always the compact row: dropdown + Connect/Disconnect, whether
            or not the site is connected. Pick a saved site to load its credentials, then Connect;
            add a brand-new site from Settings → Add Website. The button aligns to the bottom so it
            sits level with the dropdown field, not the "Site Storage" label stacked above it. */}
        {(() => {
          const connectDisabled = loading || (!connected && !creds.appPassword);
          return (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <SiteStorageSelect onChange={onChange} onSwitchSite={onSwitchSite} refreshToken={(sitesRefreshToken ?? 0) + saveTick} loading={connecting} />
            </div>
            <button
              onClick={connected ? () => onDisconnect?.() : connect}
              disabled={connectDisabled}
              onMouseEnter={() => setHoverConn(true)}
              onMouseLeave={() => setHoverConn(false)}
              className="credentials-connect-btn"
              style={{
                ...coverBtnStyle(
                  loading ? "linear-gradient(135deg,#1d4ed8,#2563eb)"
                    : connected
                      ? (hoverConn ? "linear-gradient(135deg,#b91c1c,#e11919)" : "linear-gradient(135deg,#15803d,#16a34a)")
                      : (hoverConn ? "linear-gradient(135deg,#1e40af,#1d4ed8)" : "linear-gradient(135deg,#1d4ed8,#2563eb)")
                ),
                padding: "6px 18px", fontSize: 11, marginTop: 0, borderRadius: 999, whiteSpace: "nowrap",
                alignSelf: "flex-end",
                opacity: connectDisabled ? 0.55 : 1,
                cursor: connectDisabled ? "not-allowed" : "pointer",
                boxShadow: hoverConn
                  ? (connected ? "0 4px 14px rgba(220,38,38,.55)" : "0 4px 14px rgba(37,99,235,.55)")
                  : (connected ? "0 2px 8px rgba(21,128,61,.35)" : "0 2px 8px rgba(37,99,235,.35)"),
                transform: hoverConn ? "translateY(-1px)" : "",
              }}
            >
              {loading ? "Testing…" : connected ? (hoverConn ? "Disconnect" : "Connected") : "Connect"}
            </button>
          </div>
          );
        })()}

        {/* Status indicator — only when connected (no "WP Connection" label otherwise) */}
        {connected && info?.ok && (
          <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 14, marginBottom: 2 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span className="conn-status-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399", display: "inline-block", flexShrink: 0 }} />
              <span className="conn-status-text" style={{ fontSize: 10, color: "#34d399", fontWeight: 700, fontFamily: "Roboto Mono, monospace" }}>
                Connected · {pluginLabel(info.plugin)} · {builderLabel(info.builder)}
              </span>
            </div>
          </div>
        )}

        {/* Connection warning (e.g. SEO meta not REST-writable) — pinned to the LEFT of this
            panel and absolutely positioned, so it never pushes the fields/buttons down or
            overlaps the cover divider under Run Full Audit. */}
        {connected && info?.ok && info.noRoleOnSite && (
          <div style={{ position: "absolute", top: 0, right: "calc(100% + 18px)", width: 260, textAlign: "right", zIndex: 11 }}>
            <span style={{ fontSize: 9.5, color: "#f87171", fontWeight: 700, fontFamily: "Mulish, sans-serif", lineHeight: 1.45, display: "block" }}>
              ⚠ This login authenticated but has no user role on this website. Add the user to this site (Users → Add Existing) with an Editor or Administrator role. Pages, edits, and crawls won&rsquo;t work until then.
            </span>
          </div>
        )}

        {connected && info?.ok && !info.noRoleOnSite && info.metaWritable === false && (
          <div style={{ position: "absolute", top: 0, right: "calc(100% + 18px)", width: 250, textAlign: "right", zIndex: 11 }}>
            <span style={{ fontSize: 9.5, color: "#fbbf24", fontFamily: "Mulish, sans-serif", lineHeight: 1.45, display: "block" }}>
              ⚠ SEO meta titles/descriptions are not REST-writable for {pluginLabel(info.plugin)} on this site — those edits won't apply automatically (H1 still works). {info.plugin === "aioseo" ? "AIOSEO stores SEO data in its own table — install/activate the Local SEO Garage plugin (v1.10+) to enable writing it." : info.plugin === "unknown" ? "No supported SEO plugin detected." : "Install/activate the Local SEO Garage plugin (v1.9+) to enable writing it."}
            </span>
          </div>
        )}


        {/* Connection error — pinned to the LEFT of the panel (like the warnings above) and
            absolutely positioned so a long message never pushes the fields/buttons down or
            overlaps the cover divider under Run Full Audit. */}
        {info && !info.ok && (
          <div style={{ position: "absolute", top: 0, right: "calc(100% + 18px)", width: 260, textAlign: "right", zIndex: 11 }}>
            <span style={{ fontSize: 9.5, color: "#f87171", fontWeight: 700, fontFamily: "Mulish, sans-serif", lineHeight: 1.45, display: "block" }}>✗ {info.error}</span>
          </div>
        )}

        {!connectOnly && (() => {
          // Greyed + disabled until a Get Competitors crawl has completed (canRunAudit). A paused or
          // running audit is exempt (those imply the crawl already happened).
          const runBlocked = canRunAudit === false && !auditRunning && !auditPaused;
          const runAuditUI = (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: runSlot ? 0 : 14 }}>
          <button
            onClick={auditRunning || runBlocked ? undefined : auditPaused ? onResumeAudit : onRunAudit}
            disabled={auditRunning || runBlocked}
            title={runBlocked ? "Run “Get Competitors” first — it crawls your site and competitors, which the full audit needs." : undefined}
            className="run-full-audit-btn"
            style={{
              width: "100%",
              padding: "9px 20px",
              // While running, the button IS the progress bar — but it keeps the same green
              // as when idle: a brighter green fills up to the percent over the base green.
              // When paused it turns amber to read as a resumable "Resume Audit".
              // Accent colors come from CSS variables (--rfa-*) defined on .run-full-audit-btn in
              // app.css: orange by default, sky-blue in dark mode. Routing the running-progress
              // gradient, idle fill, and shadow through the same vars keeps the fill intact.
              background: runBlocked
                ? "#334155"
                : auditRunning
                  ? `linear-gradient(to right, var(--rfa-fill) 0%, var(--rfa-fill) ${auditProgress}%, var(--rfa-base) ${auditProgress}%, var(--rfa-base) 100%)`
                  : auditPaused ? "#d97706" : "var(--rfa-base)",
              color: "#fff",
              border: "none",
              borderRadius: 0,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".3px",
              cursor: auditRunning || runBlocked ? "not-allowed" : "pointer",
              opacity: runBlocked ? 0.55 : 1,
              fontFamily: "'Inter', 'Mulish', sans-serif",
              whiteSpace: "nowrap", // never wrap to two lines (would shove the Stop button into the divider)
              boxShadow: runBlocked || auditRunning ? "none" : auditPaused ? "0 2px 8px rgba(217,119,6,.35)" : "0 2px 8px var(--rfa-glow)",
              transition: "background .3s ease",
            }}
          >
            {auditRunning ? `Running Audit… ${auditProgress}%` : auditPaused ? "⏯ Resume Audit" : "▶ Run Full Audit"}
          </button>
          {onSaveReport && (
            // Save/export icon sits above the Launch panel's top border, its right edge aligned
            // with the panel's right border (absolute, anchored to the position:relative .cov-panel).
            <div style={{ position: "absolute", top: -38, right: 0, zIndex: 12 }}
              onMouseEnter={() => setShowSaveTip(true)}
              onMouseLeave={() => setShowSaveTip(false)}
            >
              <button
                onClick={() => { if (canSaveReport) setShowSaveMenu(v => !v); }}
                disabled={!canSaveReport}
                style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "9px 8px", background: "none", border: "none", borderRadius: 6, lineHeight: 0, color: canSaveReport ? "#16a34a" : "#94a3b8", cursor: canSaveReport ? "pointer" : "not-allowed", boxShadow: "none", opacity: canSaveReport ? 1 : 0.45, filter: "none", transition: "color .15s, transform .15s" }}
                onMouseEnter={e => { if (!canSaveReport) return; e.currentTarget.style.color = "#15803d"; e.currentTarget.style.transform = "scale(1.12)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = canSaveReport ? "#16a34a" : "#94a3b8"; e.currentTarget.style.transform = "scale(1)"; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={canSaveReport ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
              </button>
              {showSaveTip && !showSaveMenu && (
                <div style={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0, zIndex: 600, width: 250, background: pop.bg, border: pop.border, borderRadius: 8, boxShadow: pop.shadow, padding: "9px 11px", fontSize: 12, lineHeight: 1.5, color: pop.text, fontFamily: "'Inter','Mulish',sans-serif", fontWeight: 400, textTransform: "none", letterSpacing: "normal", whiteSpace: "normal" }}>
                  Enabled once at least one tab has crawled data. It exports whichever tabs currently have crawled data as a PDF or HTML — the sections appear in the same order as the tools in the sidebar.
                </div>
              )}
              {showSaveMenu && canSaveReport && (
                <div
                  style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 500, background: pop.bg, border: pop.border, borderRadius: 8, boxShadow: pop.shadow, overflow: "hidden", minWidth: 150, whiteSpace: "nowrap" }}
                  onMouseLeave={() => setShowSaveMenu(false)}
                >
                  <div style={{ padding: "8px 12px 4px", fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: pop.heading, fontFamily: "'Inter','Mulish',sans-serif" }}>Export report</div>
                  {([{ label: "📄  Save as PDF", fmt: "pdf" as const }, { label: "🌐  Save as HTML", fmt: "html" as const }]).map(({ label, fmt }) => (
                    <button key={fmt} onClick={() => { setShowSaveMenu(false); onSaveReport(fmt); }}
                      style={{ display: "block", width: "100%", padding: "10px 14px", background: "transparent", border: "none", borderTop: `1px solid ${pop.sep}`, color: pop.text, fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left", fontFamily: "'Inter','Mulish',sans-serif" }}
                      onMouseEnter={e => { e.currentTarget.style.background = pop.hover; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >{label}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(auditRunning || auditPaused) && (
            <button
              onClick={onStopAudit}
              style={{
                width: "100%",
                marginTop: 6,
                padding: "8px 14px",
                background: "#991b1b",
                color: "#fff",
                border: "none",
                borderRadius: 5,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "Mulish, sans-serif",
                letterSpacing: 0.5,
              }}
            >
              {auditPaused ? "✕ Discard Audit" : "✕ Stop Audit"}
            </button>
          )}
        </div>
          );
          return runSlot ? createPortal(runAuditUI, runSlot) : runAuditUI;
        })()}
      </div>
    );
  }

  // ── Default bar mode (above nav) ──────────────────────────────
  return (
    <div style={{ background: "#0f1117", borderBottom: "1px solid rgba(255,255,255,.06)", padding: "0 52px" }}>
      {/* Site Storage — switch report (above the WP Connection label) */}
      <SiteStorageSelect onChange={onChange} onSwitchSite={onSwitchSite} refreshToken={(sitesRefreshToken ?? 0) + saveTick} loading={connecting} compact />
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0", fontSize: 11 }}>
        <span style={{ fontFamily: "Roboto Mono, monospace", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,.25)" }}>
          WP Connection
        </span>
        {connected && info?.ok ? (
          <>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#34d399", fontWeight: 700 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399", display: "inline-block" }} />
              {info.user} · {pluginLabel(info.plugin)} · {builderLabel(info.builder)} · {info.siteTitle && `"${info.siteTitle}"`}
            </span>
            <button onClick={() => setOpen(o => !o)} style={btnStyle("#1e293b")}>
              {open ? "▲ Hide" : "Change Site"}
            </button>
          </>
        ) : (
          <>
            <span style={{ color: "rgba(255,255,255,.35)" }}>Not connected — enter credentials to enable fix execution</span>
            <button onClick={() => setOpen(true)} style={btnStyle("#2563eb")}>Connect →</button>
          </>
        )}
        {info && !info.ok && <span style={{ color: "#f87171", fontSize: 10 }}>✗ {info.error}</span>}
      </div>

      {open && (
        <div style={{ paddingBottom: 12, display: "grid", gridTemplateColumns: "1.73fr 1.2fr", gap: 12, alignItems: "start" }}>
          {/* Left: Site URL + Username stacked */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {fields.slice(0, 2).map(f => (
              <div key={f.key}>
                <label style={{ display: "block", fontSize: 9, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 3 }}>{f.label}</label>
                <input
                  type={f.type}
                  value={creds[f.key]}
                  onChange={e => onChange({ ...creds, [f.key]: e.target.value })}
                  placeholder={f.ph}
                  style={{ width: "100%", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 5, padding: "5px 9px", color: "#e2e8f0", fontSize: 11, fontFamily: "Roboto Mono, monospace", outline: "none", boxSizing: "border-box" }}
                />
              </div>
            ))}
          </div>
          {/* Right: App Password + Connect button stacked */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <label style={{ display: "block", fontSize: 9, color: "rgba(255,255,255,.3)", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 3 }}>{fields[2].label}</label>
              <input
                type={fields[2].type}
                value={creds[fields[2].key]}
                onChange={e => onChange({ ...creds, [fields[2].key]: e.target.value })}
                placeholder={fields[2].ph}
                style={{ width: "100%", background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 5, padding: "5px 9px", color: "#e2e8f0", fontSize: 11, fontFamily: "Roboto Mono, monospace", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <button onClick={connect} disabled={loading || !creds.appPassword}
              style={{ ...btnStyle("linear-gradient(135deg,#1d4ed8,#2563eb)", true), width: "65%", alignSelf: "center", transition: "transform .15s, box-shadow .15s", borderRadius: 8 }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.background = "linear-gradient(135deg,#1e40af,#1d4ed8)"; b.style.boxShadow = "0 4px 14px rgba(37,99,235,.55)"; b.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.background = "linear-gradient(135deg,#1d4ed8,#2563eb)"; b.style.boxShadow = ""; b.style.transform = ""; }}
            >
              {loading ? "Testing…" : "Connect →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// One option in the switcher — a site keyed by normalized host. A saved WP site also carries
// its saved-site id so selecting it can populate credentials.
interface SiteOption { key: string; label: string; savedId?: string; siteUrl?: string; username?: string; }

// Site Storage: pick a site to load its report (and connect its WP login if saved), or save
// the current credentials. Lists the union of saved WP sites and audit report buckets, keyed
// by normalized host. Sits above the "WP Connection" label.
function SiteStorageSelect({ onChange, onSwitchSite, refreshToken, compact, loading }: {
  onChange: (c: Credentials) => void;
  onSwitchSite?: (key: string, opts?: { siteUrl?: string; label?: string }) => void;
  refreshToken?: number;
  compact?: boolean;
  loading?: boolean;
}) {
  const [options, setOptions] = useState<SiteOption[]>([]);
  const [active, setActive] = useState(getActiveSiteKey());
  const [msg, setMsg] = useState("");
  // Show the spinner the instant a site is picked (before App remounts + the parent's `loading`
  // auto-connect kicks in). Safety-cleared in case a switch errors and no remount happens.
  const [switching, setSwitching] = useState(false);
  // Follow the app theme (the cover panel recolors between light/dark).
  const [isLight, setIsLight] = useState(() => document.documentElement.getAttribute("data-theme") === "light");
  useEffect(() => {
    const sync = () => setIsLight(document.documentElement.getAttribute("data-theme") === "light");
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  async function refresh() {
    const byKey = new Map<string, SiteOption>();
    // Saved WP sites (carry credentials).
    try {
      const saved: SiteSummary[] = await listSites();
      for (const s of saved) {
        const key = siteKey(s.siteUrl);
        byKey.set(key, { key, label: s.label || key, savedId: s.id, siteUrl: s.siteUrl, username: s.username });
      }
    } catch { /* ignore */ }
    // Report buckets (may include ad-hoc audited domains with no saved WP login).
    try {
      const { sites, activeSiteKey } = await fetchAuditList();
      for (const b of sites) {
        const existing = byKey.get(b.siteKey);
        if (existing) { if (!existing.label) existing.label = b.label; }
        else byKey.set(b.siteKey, { key: b.siteKey, label: b.label || b.siteKey });
      }
      if (activeSiteKey) setActive(activeSiteKey);
    } catch { /* ignore */ }
    setOptions([...byKey.values()].sort((a, b) => a.label.localeCompare(b.label)));
  }
  useEffect(() => { void refresh(); }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSelect(key: string) {
    setMsg("");
    if (!key) return;
    setSwitching(true); // spinner shows immediately on selection; the remount then keeps it going
    window.setTimeout(() => setSwitching(false), 15000); // fail-safe if a switch errors (no remount)
    const opt = options.find(o => o.key === key);
    // If it's a saved WP site, populate the credential fields.
    if (opt?.savedId) {
      try {
        const site = await getSite(opt.savedId);
        onChange({ siteUrl: site.siteUrl, username: site.username, appPassword: site.appPassword });
      } catch { setMsg("Could not load that saved site's credentials."); }
    }
    // Load that site's report (App remounts).
    setActive(key);
    onSwitchSite?.(key, { siteUrl: opt?.siteUrl, label: opt?.label });
  }

  const labelStyle: React.CSSProperties = { fontSize: 9, color: isLight ? "#64748b" : (compact ? "rgba(255,255,255,.3)" : "rgba(255,255,255,.4)"), textTransform: "uppercase", letterSpacing: compact ? 2 : 1.5, fontWeight: 700, fontFamily: "Mulish, sans-serif" };
  const selStyle: React.CSSProperties = {
    flex: 1, minWidth: 0, borderRadius: 5, padding: "5px 9px", fontSize: 11, fontFamily: "Mulish, sans-serif", outline: "none", cursor: "pointer",
    background: isLight ? "#ffffff" : "rgba(255,255,255,.06)",
    border: isLight ? "1px solid #cbd5e1" : "1px solid rgba(255,255,255,.12)",
    color: isLight ? "#0f172a" : "#e2e8f0",
  };

  // Native <option> popups don't inherit the select's colors on all browsers, so the dark-mode
  // list rendered near-white text on a default light background (illegible). Set them explicitly.
  const optStyle: React.CSSProperties = { background: isLight ? "#ffffff" : "#1e293b", color: isLight ? "#0f172a" : "#e2e8f0" };

  // Pin the control to "" and show the ACTIVE site as the placeholder text (so the closed dropdown
  // shows which report is loaded). The option list below OMITS the active site — it's already
  // represented by the placeholder, so it must not also appear as a list item (that was the
  // "duplicate of the selected site" bug). Picking any OTHER site is always a change → runs onSelect.
  const activeOpt = options.find(o => o.key === active);
  const activeLabel = activeOpt ? `${activeOpt.label}${activeOpt.savedId ? ` (${activeOpt.username})` : ""}` : "";
  const dropdown = (
    <select value="" onChange={e => onSelect(e.target.value)} style={selStyle} title="Switch to a site's saved report">
      <option value="" style={optStyle}>{activeLabel || "Site Storage"}</option>
      {options.filter(o => o.key !== active).map(o => <option key={o.key} value={o.key} style={optStyle}>{o.label}{o.savedId ? ` (${o.username})` : ""}</option>)}
    </select>
  );

  if (compact) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 7 }}>
        <span style={labelStyle}>Site Storage</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, maxWidth: 460 }}>{(loading || switching) && <span className="lsg-spinner" title="Loading & connecting this site…" />}{dropdown}</div>
        {msg && <span style={{ fontSize: 10, color: msg.startsWith("Saved") ? "#34d399" : "#f87171", fontFamily: "Mulish, sans-serif" }}>{msg}</span>}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={labelStyle}>Site Storage</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>{(loading || switching) && <span className="lsg-spinner" title="Loading & connecting this site…" />}{dropdown}</div>
      {msg && <span style={{ fontSize: 10, color: msg.startsWith("Saved") ? "#34d399" : "#f87171", fontFamily: "Mulish, sans-serif" }}>{msg}</span>}
    </div>
  );
}

function btnStyle(bg: string, tall = false): React.CSSProperties {
  return { padding: tall ? "6px 16px" : "4px 12px", background: bg, color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "Mulish, sans-serif" };
}

function coverBtnStyle(bg: string): React.CSSProperties {
  return {
    padding: "9px 20px", background: bg, color: "#fff", border: "none", borderRadius: 8,
    fontSize: 12, fontWeight: 700, letterSpacing: ".3px", cursor: "pointer",
    fontFamily: "'Inter', 'Mulish', sans-serif", whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(37,99,235,.35)", transition: "transform .15s, box-shadow .15s",
    marginTop: 2, alignSelf: "flex-start",
  };
}
